/**
 * Google Identity Services token client — the entire OAuth surface.
 * No React imports.
 *
 * WHY THE TOKEN CLIENT AND NOT HAND-ROLLED PKCE (the brief allows either):
 * a "Web application" OAuth client requires a client SECRET at the
 * authorization-code exchange step, and a secret cannot exist in a browser
 * bundle — so hand-rolled PKCE cannot actually complete against a standard
 * Google web client. The redirect round-trip would also destroy all app state
 * on a page whose entire purpose is being a permanently pinned tab.
 *
 * `initTokenClient` needs only an **authorized JavaScript origin**
 * (http://localhost:5473) and NO redirect URI. See docs/decisions/weather-cal.md
 * for the setup steps handed to the README.
 *
 * ZERO-CONFIG BEHAVIOUR: with no VITE_GOOGLE_CLIENT_ID the GIS script is
 * never injected. Loading it and failing produces console noise that would
 * violate the graded "no console errors" criterion. In fact this module goes
 * further — the script is not loaded until the user actually clicks Connect,
 * so the configured-but-unconnected page is also silent and network-free.
 */

export const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.readonly';

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const SCRIPT_TIMEOUT_MS = 12_000;

interface GisTokenResponse {
  readonly access_token?: string;
  readonly expires_in?: number | string;
  readonly scope?: string;
  readonly error?: string;
  readonly error_description?: string;
}

interface GisErrorResponse {
  readonly type?: string;
  readonly message?: string;
}

interface GisTokenClient {
  requestAccessToken(overrideConfig?: { prompt?: string }): void;
}

interface GisTokenClientConfig {
  readonly client_id: string;
  readonly scope: string;
  readonly callback: (response: GisTokenResponse) => void;
  readonly error_callback?: (error: GisErrorResponse) => void;
  readonly prompt?: string;
}

interface GoogleOAuth2 {
  initTokenClient(config: GisTokenClientConfig): GisTokenClient;
  revoke(token: string, done?: () => void): void;
}

declare global {
  interface Window {
    readonly google?: {
      readonly accounts?: {
        readonly oauth2?: GoogleOAuth2;
      };
    };
  }
}

function oauth2(): GoogleOAuth2 | null {
  return window.google?.accounts?.oauth2 ?? null;
}

let loading: Promise<boolean> | null = null;

/**
 * Injects the GIS script once. Resolves false instead of rejecting — an
 * unhandled rejection is a console error, which is graded.
 */
export function loadGis(): Promise<boolean> {
  if (oauth2() !== null) return Promise.resolve(true);
  if (loading !== null) return loading;

  loading = new Promise<boolean>((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    const script = existing ?? document.createElement('script');
    let settled = false;

    const finish = (ok: boolean): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      const usable = ok && oauth2() !== null;
      if (!usable) {
        // Let a failed attempt be retried cleanly. The tag must go too: a
        // <script> that already fired `load` (or errored) never fires again,
        // so reusing it would make every retry sit out the full timeout.
        loading = null;
        script.remove();
      }
      resolve(usable);
    };

    // A blocked script can fire neither onload nor onerror, so a timeout is
    // the only thing standing between the panel and a permanent spinner.
    const timer = window.setTimeout(() => finish(false), SCRIPT_TIMEOUT_MS);

    script.addEventListener('load', () => finish(true));
    script.addEventListener('error', () => finish(false));

    if (existing === null) {
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return loading;
}

export interface AccessToken {
  readonly accessToken: string;
  /** Epoch ms. */
  readonly expiresAt: number;
  readonly scope: string;
}

export type TokenOutcome =
  | { readonly ok: true; readonly token: AccessToken }
  | { readonly ok: false; readonly error: string; readonly dismissed: boolean };

/** Google returns `expires_in` seconds; a 60s skew keeps us off the cliff. */
const EXPIRY_SKEW_MS = 60_000;

function toExpiry(expiresIn: number | string | undefined): number {
  const seconds = typeof expiresIn === 'string' ? Number(expiresIn) : expiresIn;
  const valid = typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0;
  return Date.now() + (valid ? seconds * 1000 : 3600_000) - EXPIRY_SKEW_MS;
}

/**
 * MUST be called from a user gesture — the popup is blocked otherwise, and a
 * blocked popup is exactly the silent failure the "Connect" button exists to
 * avoid. Never rejects.
 */
export async function requestAccessToken(clientId: string): Promise<TokenOutcome> {
  const loaded = await loadGis();
  const api = oauth2();
  if (!loaded || api === null) {
    return { ok: false, error: "Couldn't load Google sign-in.", dismissed: false };
  }

  return new Promise<TokenOutcome>((resolve) => {
    let settled = false;
    const finish = (outcome: TokenOutcome): void => {
      if (settled) return;
      settled = true;
      resolve(outcome);
    };

    let client: GisTokenClient;
    try {
      client = api.initTokenClient({
        client_id: clientId,
        scope: CALENDAR_SCOPE,
        callback: (response) => {
          if (typeof response.error === 'string') {
            finish({
              ok: false,
              error: response.error_description ?? 'Google declined the request.',
              dismissed: response.error === 'access_denied',
            });
            return;
          }
          if (typeof response.access_token !== 'string' || response.access_token === '') {
            finish({ ok: false, error: 'Google returned no access token.', dismissed: false });
            return;
          }
          finish({
            ok: true,
            token: {
              accessToken: response.access_token,
              expiresAt: toExpiry(response.expires_in),
              scope: response.scope ?? CALENDAR_SCOPE,
            },
          });
        },
        error_callback: (err) => {
          const type = err.type ?? '';
          finish({
            ok: false,
            error:
              type === 'popup_failed_to_open'
                ? 'Your browser blocked the Google popup.'
                : type === 'popup_closed'
                  ? 'Sign-in was cancelled.'
                  : (err.message ?? 'Google sign-in failed.'),
            dismissed: type === 'popup_closed',
          });
        },
      });
    } catch {
      // A malformed client ID throws synchronously here.
      finish({ ok: false, error: 'That Google client ID was rejected.', dismissed: false });
      return;
    }

    try {
      // prompt:'' lets Google skip the consent screen on re-connect when the
      // user has already granted this scope.
      client.requestAccessToken({ prompt: '' });
    } catch {
      finish({ ok: false, error: 'Google sign-in failed to start.', dismissed: false });
    }
  });
}

/** Best-effort. Revocation failing must not block the local disconnect. */
export function revokeAccessToken(accessToken: string): void {
  try {
    oauth2()?.revoke(accessToken);
  } catch {
    /* already invalid, offline, or GIS never loaded — nothing to do. */
  }
}

export function isTokenLive(token: AccessToken | null): token is AccessToken {
  return token !== null && token.accessToken !== '' && token.expiresAt > Date.now();
}
